import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { IWidgetComponent } from "@main/reporting.v3/core/widgets/widget-component";
import { WidgetGridComponent } from "@main/reporting.v3/widget.grid/components/widget-grid.component";
import { WidgetHtmlComponent } from "@main/reporting.v3/widget.html/components/widget-html.component";
import { WidgetChartComponent } from "@main/reporting.v3/widget.chart/components/widget-chart.component";
import {
    WidgetControlSetComponent
} from "@main/reporting.v3/widget.control-set/components/widget-control-set.component";
import { IWidgetSourcePreload, WidgetOutputMode, WidgetSourceType } from "../core/reporting-models";
import { JsReportConfiguration } from "@main/pdf-exporting/js-report/js-report.models";
import { DashboardContextService } from "../dashboard/api/dashboard-context.service";
import { WidgetChartService } from "../widget.chart/api/widget-chart.service";
import { WidgetControlSetViewService } from "../widget.control-set/api/widget-control-set-view.service";
import { WidgetGridDataService } from "../widget.grid/api/widget-grid-data.service";
import { WidgetGridViewService } from "../widget.grid/api/widget-grid-view.service";
import { WidgetHtmlApiService } from "../widget.html/api/widget-html-api.service";
import { IStaticVariable } from "@main/reporting.v3/core/variables/variable-models";
import { IPdfExportable } from "@main/reporting.v3/core/exporting/pdf-exportable";
import { IXlsExportable } from "@main/reporting.v3/core/exporting/xls-exportable";
import { WidgetSourceService } from "../api/widget-source.service";
import { BehaviorSubject } from "rxjs";


@Component({
    selector: "reporting-widget",
    templateUrl: "./widget.component.html",
    styleUrls: ["./widget.component.scss"],
    providers: [
        WidgetControlSetViewService,
        DashboardContextService,
        WidgetGridDataService,
        WidgetGridViewService,
        WidgetHtmlApiService,
        WidgetChartService,
    ]
})
export class WidgetComponent implements IWidgetComponent, IPdfExportable, IXlsExportable {
    // Inner widget component.
    private widgetComponent: IWidgetComponent;

    // Shows or hides exporting button.
    @Input() exportable: boolean = true;

    // Determines which HTML code should be used for rendering: web view of pdf exporting view.
    @Input() output: WidgetOutputMode = "web";

    @Input() widgetType: WidgetSourceType;
    @Input() widgetSourceId: string;
    @Input() queryProvider: boolean;
    @Input() queryRequester: boolean;
    @Input() widgetRenderer: boolean;
    @Input() variableEmitter: boolean;
    @Input() variableReceiver: boolean;
    @Input() ignoreDisplayRule: boolean;

    // Set of fixed-value variables to pass to inner widget.
    @Input() staticVariables: IStaticVariable[] = [];

    // Emitter to notify when inner widget component is ready or changes.
    @Output() readonly widgetComponentChange: EventEmitter<IWidgetComponent> = new EventEmitter<IWidgetComponent>();

    constructor(private widgetSourceService: WidgetSourceService,
                private elementRef: ElementRef) {
    }

    @ViewChild(WidgetControlSetComponent) set widgetControlSetComponent(controlSet: WidgetControlSetComponent) {
        if (!controlSet) return;
        this.widgetComponent = controlSet;
        this.widgetComponentChange.emit(controlSet);
    }

    @ViewChild(WidgetChartComponent) set widgetChartComponent(chart: WidgetChartComponent) {
        if (!chart) return;
        this.widgetComponent = chart;
        this.widgetComponentChange.emit(chart);
    }

    @ViewChild(WidgetGridComponent) set widgetGridComponent(grid: WidgetGridComponent) {
        if (!grid) return;
        this.widgetComponent = grid;
        this.widgetComponentChange.emit(grid);
    }

    @ViewChild(WidgetHtmlComponent) set widgetHtmlComponent(html: WidgetHtmlComponent) {
        if (!html) return;
        this.widgetComponent = html;
        this.widgetComponentChange.emit(html);
    }

    // Widget code is supposed to be set from outside the reporting feature.
    @Input() set widgetCode(value: string) {
        this.refreshWidgetByCode(value).then();
    }

    get widgetCode(): string {
        return this.widgetComponent?.widgetCode;
    }

    get widgetSelector(): string {
        return this.widgetComponent?.widgetSelector;
    }

    get widgetVisible(): boolean {
        return !!this.widgetComponent?.widgetVisible;
    }

    get pdfViewVisible(): boolean {
        return !!(this.widgetComponent as unknown as IPdfExportable)?.pdfViewVisible;
    }

    get xlsViewVisible(): boolean {
        return !!(this.widgetComponent as unknown as IXlsExportable)?.xlsViewVisible;
    }

    get pdfExportable(): boolean {
        return !!(this.widgetComponent as unknown as IPdfExportable)?.generatePdf;
    }

    get xlsExportable(): boolean {
        return !!(this.widgetComponent as unknown as IXlsExportable)?.generateXls;
    }

    get pdfExportingLoading(): BehaviorSubject<boolean> {
        return (this.widgetComponent as unknown as IPdfExportable)?.pdfExportingLoading;
    }

    get xlsExportingLoading(): BehaviorSubject<boolean> {
        return (this.widgetComponent as unknown as IXlsExportable)?.xlsExportingLoading;
    }

    get widgetClass(): string {
        return !!this.widgetCode ? `widget-${this.widgetCode}` : "";
    }

    private async refreshWidgetByCode(code: string): Promise<void> {
        const widgetSource: IWidgetSourcePreload =
            await this.widgetSourceService.getWidgetSourcePreload(code, { showErrorMessage: false });

        this.widgetType = widgetSource?.widgetType;
        this.widgetSourceId = widgetSource?.id;
    }

    public async getPdfConfiguration(): Promise<JsReportConfiguration> {
        if (!this.pdfExportable) return;

        // If this widget is not exportable to pdf, return null.
        const configuration: JsReportConfiguration =
            await (this.widgetComponent as unknown as IPdfExportable)?.getPdfConfiguration();

        if (configuration == null) return configuration;

        // If for some reason this widget wrapper outer html is not accessible, return the base pdf configuration.
        const html: string = this.elementRef?.nativeElement?.outerHTML;
        if (!html?.length) return configuration;

        // Create a new document with a copy of this element outer html.
        const parser: DOMParser = new DOMParser();
        const doc: Document = parser.parseFromString(html, "text/html");
        const element: Element = doc.querySelector(this.widgetComponent.widgetSelector);

        // Replace inner widget component html by its pdf view html.
        element.innerHTML = configuration.template ?? "";

        // Return a new pdf configuration with this document inner html.
        // This fixes the original pdf configuration to consider this widget wrapper html code.
        return { ...configuration, template: doc.body.innerHTML };
    }

    public generatePdf(): Promise<void> {
        if (!this.pdfExportable || !this.exportable) return;
        return (this.widgetComponent as unknown as IPdfExportable)?.generatePdf();
    }

    public generateXls(): Promise<void> {
        if (!this.xlsExportable || !this.exportable) return;
        return (this.widgetComponent as unknown as IXlsExportable)?.generateXls();
    }
}